import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { PagesID } from "@utils/utilsConfigurations";
import { SelectedTargetItem } from "../../types";
import {
  UserListItem,
  getUserDisplayName,
  getUserId,
  useUsersList,
} from "../../hooks/useUsersList";
import UserRow from "./rows/UserRow";

const PAGE_SIZE = 10;

interface UsersTabProps {
  corporateId: string | number;
  selected: SelectedTargetItem[];
  onChange: (next: SelectedTargetItem[]) => void;
}

const UsersTab: React.FC<UsersTabProps> = ({
  corporateId,
  selected,
  onChange,
}) => {
  const { t } = useTranslation("enrollment-modal");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const { items, totalItems, totalPages, isLoading } = useUsersList({
    corporateId,
    page,
    pageSize,
    search: search || undefined,
    fieldFilters,
  });

  const selectedSet = useMemo(
    () => new Set<string | number>(selected.map((s) => s.id)),
    [selected],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((u) => getUserId(u) === String(id));
      if (!found) return;
      onChange([
        ...selected,
        { id, label: getUserDisplayName(found), meta: found },
      ]);
    } else {
      onChange(selected.filter((s) => s.id !== id));
    }
  };

  return (
    <Box>
      <SearchableMultiSelectListCst<UserListItem>
        items={items}
        getId={(u) => getUserId(u)}
        renderRow={({ item }) => (
          <UserRow
            id={getUserId(item)}
            name={getUserDisplayName(item)}
            email={item.email}
            status={item.status}
          />
        )}
        selectedIds={selectedSet}
        onToggle={handleToggle}
        initialSearchValue={search}
        onSearchSubmit={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder={t("targets.users.search-placeholder") || "Nome"}
        searchButtonLabel={t("targets.search-button") || "Cerca"}
        hideSearch
        headerSlot={
          <FiltersAccordionToggle
            pageId={PagesID["corporate.customers"]}
            value={fieldFilters}
            onApply={(next) => {
              setFieldFilters(next);
              setPage(1);
            }}
          />
        }
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(1);
        }}
        isLoading={isLoading}
        selectedCountLabel={
          t("targets.selected-count", { count: selected.length }) ||
          `${selected.length} elementi selezionati`
        }
        emptyLabel={t("targets.users.empty") || "Nessun utente trovato"}
      />
    </Box>
  );
};

export default UsersTab;
